import { useCallback, useEffect, useMemo, useState } from 'react';

import { useUsers, useRoles, useCompanies } from '@adsk/alloy-provider';
import { safeSearchRegex } from '@adsk/alloy-react-helpers';

import {
  MentionData,
  MentionType,
  MENTION_TYPES,
  MentionSupportedTypes,
  SLICE_START,
  defaultFilter,
  defaultToMentionData,
  defaultTypeToSize,
} from '../consts';

export interface UseMentionSuggestionsOptions {
  filter?: <T extends MentionSupportedTypes>(
    data: T[],
    type: MentionType
  ) => T[];
  toMentionData?: (
    data: MentionSupportedTypes[],
    type: MentionType
  ) => MentionData[];
  typeToSize?: (type: MentionType) => number;
}

function useMentionSuggestions(options?: UseMentionSuggestionsOptions) {
  const {
    filter = defaultFilter,
    toMentionData = defaultToMentionData,
    typeToSize = defaultTypeToSize,
  } = options || {};
  const [suggestions, setSuggestions] = useState<MentionData[]>([]);
  const [search, setSearch] = useState('');
  const users = useUsers();
  const roles = useRoles();
  const companies = useCompanies();

  const userSuggestions = useMemo(
    () =>
      toMentionData(
        filter(users.data, MENTION_TYPES.USER).slice(
          SLICE_START,
          typeToSize(MENTION_TYPES.USER)
        ),
        MENTION_TYPES.USER
      ),
    [toMentionData, filter, users.data, typeToSize]
  );

  const companySuggestions = useMemo(
    () =>
      toMentionData(
        filter(companies.data, MENTION_TYPES.COMPANY).slice(
          SLICE_START,
          typeToSize(MENTION_TYPES.COMPANY)
        ),
        MENTION_TYPES.COMPANY
      ),
    [toMentionData, filter, companies.data, typeToSize]
  );

  const roleSuggestions = useMemo(
    () =>
      toMentionData(
        filter(roles.data, MENTION_TYPES.ROLE).slice(
          SLICE_START,
          typeToSize(MENTION_TYPES.ROLE)
        ),
        MENTION_TYPES.ROLE
      ),
    [toMentionData, filter, roles.data, typeToSize]
  );

  const searchUserSuggestions = useCallback(
    (searchQuery: string) =>
      toMentionData(
        filter(users.data, MENTION_TYPES.USER)
          .filter(({ name }) => safeSearchRegex(searchQuery).test(name))
          .slice(SLICE_START, typeToSize(MENTION_TYPES.USER)),
        MENTION_TYPES.USER
      ),
    [filter, toMentionData, typeToSize, users.data]
  );

  const searchCompanySuggestions = useCallback(
    (searchQuery: string) =>
      toMentionData(
        filter(companies.data, MENTION_TYPES.COMPANY)
          .filter(({ name }) => safeSearchRegex(searchQuery).test(name))
          .slice(SLICE_START, typeToSize(MENTION_TYPES.COMPANY)),
        MENTION_TYPES.COMPANY
      ),
    [toMentionData, filter, companies.data, typeToSize]
  );

  const searchRoleSuggestions = useCallback(
    (searchQuery: string) =>
      toMentionData(
        filter(roles.data, MENTION_TYPES.ROLE)
          .filter(({ name }) => safeSearchRegex(searchQuery).test(name))
          .slice(SLICE_START, typeToSize(MENTION_TYPES.ROLE)),
        MENTION_TYPES.ROLE
      ),
    [toMentionData, filter, roles.data, typeToSize]
  );

  const onSearchChange = useCallback(
    ({ value }: { value: string }) => {
      setSearch(value);
      setSuggestions([
        ...searchUserSuggestions(value),
        ...searchCompanySuggestions(value),
        ...searchRoleSuggestions(value),
      ]);
    },
    [searchCompanySuggestions, searchRoleSuggestions, searchUserSuggestions]
  );

  useEffect(() => {
    if (!search) {
      setSuggestions([
        ...userSuggestions,
        ...companySuggestions,
        ...roleSuggestions,
      ]);
    } else {
      onSearchChange({ value: search });
    }
  }, [
    userSuggestions,
    companySuggestions,
    roleSuggestions,
    search,
    searchUserSuggestions,
    searchCompanySuggestions,
    searchRoleSuggestions,
    toMentionData,
    onSearchChange,
    users.loading,
    companies.loading,
    roles.loading,
  ]);

  return {
    isLoading: users.loading || companies.loading || roles.loading,
    suggestions,
    onSearchChange,
  };
}

export default useMentionSuggestions;
